import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  GasPump as Fuel,
  Gear as Cog,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  Gauge,
  Flame,
  ArrowRight,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { CardRibbon } from '@/components/ui/card-ribbon';
import { Card, CardContent } from './ui/card';
import CarImagePlaceholder from './CarImagePlaceholder';
import { browserControlWS } from '../services/browserControlWebSocket';
import { parseEngineCC } from '../utils/engine';
import { useOptimizedImageStatic } from '../hooks/useOptimizedImage';
import { usePhotoGallery } from '../hooks/usePhotoGallery';
import { useContactInfo } from '../hooks/useContactInfo';
import { useTranslation } from '../i18n/I18nContext';
import { useShowcaseText } from '../i18n/useShowcaseText';
import { useTranslateVehicleValue } from '../utils/vehicleValues';
import { useDutyFree, getFinancedAmount } from '../context/DutyFreeContext';
import { config } from '../config/runtimeConfig';
import { openWhatsApp } from '../utils/whatsapp';
import { formatRegYear } from '../utils/registrationYear';
import { getDisplayImages } from '../utils/carImage';
import './CarCard.css';
import { carTitle } from '../utils/carTitle';
import { formatEuro, numberLocale } from '../utils/formatMoney';

const CarCard = ({
  car,
  forceShowSoldBadge = false,
  onCardClick = null,
  showMonthlyRates = false,
  swipePhotos = true,
}) => {
  const navigate = useNavigate();
  // Язык прокидываем в подписи (t) и в значения спеков (translateVehicleValue) —
  // инвентарь Autodis лежит по-французски. Английский fallback в t(...)
  // держит карточку по-английски вне I18nProvider / для англоязычных
  // дилеров (Andys/ShiftGears), где перевода нет (#1086).
  const { t, language } = useTranslation();
  // Деньги и пробег на витрине — по языку («15 990 €»), как на странице
  // машины (#1335). У дилеров формат прежний: `€15,990`.
  const money = config.IS_OWN_STOCK
    ? (value) => formatEuro(value, language)
    : (value) => `€${value.toLocaleString()}`;
  const plainNumber = config.IS_OWN_STOCK
    ? (value) => value.toLocaleString(numberLocale(language))
    : (value) => value.toLocaleString();
  const showcaseText = useShowcaseText();
  const translateVehicleValue = useTranslateVehicleValue();
  const {
    isDutyFree,
    calculateDutyFree,
    calculateMonthlyPayment,
    bfMaxFinancing,
    priceLabel,
  } = useDutyFree();
  // Display images: для new-model (Autodis OMODA/JAECOO, issue #884)
  // landscapeImage prepended так чтобы первый thumbnail совпадал с hero
  // photo на лендинге. Used-VIN — обычный car.images[].
  const displayImages = React.useMemo(() => getDisplayImages(car), [car]);
  // Shared gallery logic (index + swipe) — same hook powers the VDP main photo.
  const {
    index: currentImageIndex,
    setIndex: setCurrentImageIndex,
    next: nextImage,
    prev: prevImage,
    didSwipeRef,
    swipeHandlers,
  } = usePhotoGallery(displayImages.length);
  const firstImage =
    displayImages.length > 0 ? displayImages[currentImageIndex] : null;
  // Explicitly disable lazy loading to avoid race condition with widget runtimeConfig
  // The config.ENVIRONMENT_NAME may not be 'widget' yet when hook initializes
  // `src` is the resized photo; the original only if resizing failed (autodis
  // demo: local imgproxy can't proxy external https URLs without an explicit
  // allowlist). While the resize URL is on its way there is no `src` and the
  // media box stays empty — showing the original meanwhile made every card
  // download it as well as the resized copy (#1469).
  const { src: displaySrc, pending: photoPending, ref } = useOptimizedImageStatic(
    firstImage,
    650,
    400,
    { lazy: false }
  );

  /**
   * Снимок, который НЕ ЗАГРУЗИЛСЯ, показывает ту же заглушку, что и снимок,
   * которого нет вовсе.
   *
   * Без этого браузер рисовал свой значок «битая картинка» с подписью — на
   * витрине из сотни карточек это читается сломанным продуктом, а не
   * недоступным файлом. Заглушка уже есть (`CarImagePlaceholder`), ей не
   * хватало только повода показаться.
   *
   * Ключ по URL: у карточки есть карусель, и после перелистывания на
   * рабочий снимок заглушка должна уйти.
   *
   * Превью, которое не загрузилось (CDN недоступен, imgproxy не достал файл),
   * сначала уступает оригиналу и только потом заглушке (#1469).
   */
  const [brokenSrcs, setBrokenSrcs] = React.useState(() => new Set());
  let shownSrc = null;
  if (displaySrc && !brokenSrcs.has(displaySrc)) shownSrc = displaySrc;
  // Не пока подписывается следующий снимок карусели: `displaySrc` тогда — ещё
  // превью предыдущего, а `firstImage` — уже оригинал следующего.
  else if (displaySrc && !photoPending && firstImage && !brokenSrcs.has(firstImage)) shownSrc = firstImage;
  const markBroken = (src) => setBrokenSrcs((prev) => new Set(prev).add(src));
  const contactInfo = useContactInfo();

  const hasMultipleImages = displayImages.length > 1;

  // Arrow handlers wrap the shared next/prev to also stop the card's
  // navigate-to-VDP click (arrows live inside the clickable photo).
  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    prevImage();
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    nextImage();
  };

  // Парсим специальные маркеры из названия модели
  const parseCarInfo = () => {
    const model = car.model || '';

    // Парсим "Reduced from X€" из названия как fallback
    const reducedMatch = model.match(/Reduced from (\d+)€/i);
    const parsedOldPrice = reducedMatch ? parseInt(reducedMatch[1]) : null;

    // Парсим другие маркеры
    const hasMoreUnits = /More Units Available/i.test(model);

    // Очищаем название от всех маркеров
    const cleanModel = model
      .replace(/Reduced from \d+€/gi, '')
      .replace(/More Units Available/gi, '')
      .replace(/\*/g, '')
      .trim();

    return { cleanModel, parsedOldPrice, hasMoreUnits };
  };

  const { cleanModel, parsedOldPrice, hasMoreUnits } = parseCarInfo();
  // Используем oldPrice из API (Google Sheets) как основной источник, парсинг из названия как fallback
  const oldPrice = car.oldPrice || parsedOldPrice;
  const cleanMake = car.make?.replace(/\*/g, '') || car.make;

  // Заголовок карточки: используем name из Google Sheets если есть, иначе формируем из make + model
  const displayTitle = car.name || `${cleanMake} ${cleanModel}`.trim();

  // Подпись для исходящих сообщений (WhatsApp, test drive) — cleanMake/cleanModel
  // вместо сырых полей, год опционален (#1206).
  const carLabel = carTitle({
    year: car.year,
    make: cleanMake,
    model: cleanModel,
  });

  const engineCC = parseEngineCC(car.engineDisplacement);

  // Расчитываем цену для отображения (обычная или Duty Free из инвентаря)
  const displayPrice = isDutyFree ? calculateDutyFree(car) : car.price;

  // #828 + #1070: "from €X/mo*" считается от ФИНАНСИРУЕМОЙ суммы (цена, обрезанная
  // по BF_MAX_FINANCING = €12k), а не от полной цены — чтобы совпадать с дефолтом
  // VDP (минимальный deposit). Для авто <= €12k financedAmount === displayPrice.
  const financedAmount = getFinancedAmount(
    displayPrice,
    undefined,
    bfMaxFinancing
  );

  const handleWhatsApp = () => {
    const priceText =
      displayPrice > 0
        ? `€${displayPrice.toLocaleString()}${
            isDutyFree ? ` ${priceLabel}` : ''
          }`
        : 'price on request';
    openWhatsApp({
      phone: contactInfo.whatsapp,
      message: `Hi! I'm interested in the ${carLabel} (${priceText})`,
      context: 'car_card',
    });
  };

  const handleCarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Swipe on the photo shouldn't navigate to the VDP — swallow the click
    // that the browser synthesises after a horizontal drag.
    if (didSwipeRef.current) {
      didSwipeRef.current = false;
      return;
    }

    // Пропустить отправку события, если клик инициирован агентом
    if (!window.__agentCommandInProgress) {
      // Отправить событие просмотра автомобиля (только от пользователя)
      browserControlWS.sendUserAction(
        'car-view',
        {
          carId: car.id,
          make: cleanMake,
          model: cleanModel,
          year: car.year,
          price: car.price,
        },
        false
      ); // false = не включать полный pageContext (избегаем дублирования)
    }

    // Mini-catalog mode (#828): если передан onCardClick, используем его вместо
    // react-router navigate. Это нужно для режима встраивания на хост-страницу —
    // клик ведёт на каталог клиентского сайта через window.location.href.
    if (typeof onCardClick === 'function') {
      onCardClick(car);
      return;
    }

    // Сохраняем текущий hash (с фильтрами) для кнопки "назад" на странице детали
    try {
      sessionStorage.setItem(
        'b2b-back-to-cars',
        window.location.hash.replace('#', '') || '/cars'
      );
    } catch (_) {
      /* ignore */
    }

    navigate(`/cars/${car.id}`);
  };

  const getBookTestDriveUrl = () => {
    const carInfo = carLabel;
    return `/book-test-drive?car=${encodeURIComponent(carInfo)}`;
  };

  const isSold = car.statusSold?.toLowerCase() === 'yes' || forceShowSoldBadge;
  // Свайп по фото листает его галерею. В горизонтальной ленте (главная,
  // #1456) он же забирал жест у ленты: палец на фото — а это бо́льшая часть
  // карточки — листал снимки машины, и ленту можно было сдвинуть только за
  // текст. Лента выключает его через `swipePhotos={false}`; стрелки и точки
  // галереи остаются.
  const photoSwipe = hasMultipleImages && swipePhotos;
  // Витрина ShiftGears (#1335, борд «C · Soltera»): плитка, цена
  // заголовком, одна строка «год · км · топливо» и синяя круглая стрелка
  // вместо кнопки. У дилеров карточка прежняя — у них нет флага витрины.
  const isTile = config.IS_OWN_STOCK && !showMonthlyRates;
  const mileageText =
    car.mileage != null && car.mileage >= 0
      ? `${plainNumber(car.mileage)} km`
      : null;

  return (
    <Card
      className={`car-card${isTile ? ' car-card--tile' : ''}${
        isSold ? ' car-card--sold' : ''
      }`}
    >
      <div
        ref={ref}
        className="car-card__media"
        onClick={handleCarClick}
        {...(photoSwipe ? swipeHandlers : {})}
        style={photoSwipe ? { touchAction: 'pan-y' } : undefined}
      >
        {shownSrc ? (
          <img
            src={shownSrc}
            alt={displayTitle}
            className="car-card__image"
            onError={() => markBroken(shownSrc)}
          />
        ) : photoPending ? null : (
          <CarImagePlaceholder
            make={cleanMake}
            model={cleanModel}
            className="car-card__placeholder"
          />
        )}

        {/* Ribbon badges. Обёртка — без собственных стилей в бандле: у дилеров
            ленты позиционируются от карточки, как раньше. Витрина по ней
            ставит значки В РЯД (`page-layout.css`): оба стояли в одной точке,
            и «Reserved» ложился поверх «HOT» (#1417). */}
        <div className="car-card__ribbons">
          {car.isHotOffer && (
            <CardRibbon variant="hot">
              {/* Огонёк рядом со словом — тот же знак, что и у бейджа на
                странице машины: там он уже стоит, и лента без него читалась
                другой пометкой. `fill: currentColor` — знак белый на любой
                брендовой заливке ленты. */}
              <Flame size={12} weight="bold" fill="currentColor" />
              {t('landing.card.hot', 'HOT')}
            </CardRibbon>
          )}
          {isSold && (
            <CardRibbon variant="sold">
              {t('landing.card.reserved', 'RESERVED')}
            </CardRibbon>
          )}
        </div>

        {/* Image carousel navigation */}
        {hasMultipleImages && (
          <>
            {/* Navigation arrows — desktop hover only. pointer-events-none until
                hover so on touch devices (no hover) these invisible buttons
                don't intercept edge taps/swipes — the touch falls through to the
                image (swipe / navigate). */}
            <button
              type="button"
              onClick={handlePrevImage}
              className="car-card-nav-btn car-card-nav-btn--prev"
              aria-label={showcaseText('showcase.car.prevImage', 'Previous image')}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              onClick={handleNextImage}
              className="car-card-nav-btn car-card-nav-btn--next"
              aria-label={showcaseText('showcase.car.nextImage', 'Next image')}
            >
              <ChevronRight />
            </button>

            {/* Image indicators (dots) — always visible on mobile (touch has no
                hover, and they're the affordance/target for browsing photos),
                hover-only on desktop as before. */}
            <div className="car-card-indicators">
              {displayImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`car-card__dot${
                    index === currentImageIndex ? ' car-card__dot--active' : ''
                  }`}
                  aria-label={`${showcaseText('showcase.car.goToImage', 'Go to image')} ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {isTile ? (
        <CardContent className="car-card__body">
          <p className="car-card__tile-price">
            {displayPrice > 0
              ? money(displayPrice)
              : t('landing.card.contactForPrice', 'Contact for price')}
            {!car.registrationStatus && !car.isNewModel && displayPrice > 0 && (
              <span className="car-card__note">
                {showcaseText('showcase.car.reg', '+reg')}
              </span>
            )}
            {oldPrice ? (
              <span className="car-card__note car-card__price-old">
                {money(oldPrice)}
              </span>
            ) : null}
          </p>
          <h3 className="car-card__title" onClick={handleCarClick}>
            {displayTitle}
          </h3>
          <p className="car-card__meta">
            {[
              formatRegYear(car),
              mileageText,
              translateVehicleValue(car.fuelType, 'fuelType'),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {/* Одна круглая стрелка вместо кнопки с подписью: открывает ту же
              страницу, что клик по фото и названию. Кнопка, а не украшение,
              — с клавиатуры карточку открывают ею. */}
          <button
            type="button"
            className="car-card__go"
            onClick={handleCarClick}
            aria-label={`${t('landing.card.viewDetails', 'View details')}: ${displayTitle}`}
          >
            <ArrowRight size={20} weight="bold" aria-hidden="true" />
          </button>
        </CardContent>
      ) : (
      <CardContent className="car-card__body">
        <div className="car-card__section">
          <h3 className="car-card__title" onClick={handleCarClick}>
            {displayTitle}
          </h3>
          <div className="car-card__specs">
            {/* Engine Displacement (если есть) */}
            {engineCC && (
              <>
                <div className="car-card__spec">
                  <Gauge className="car-card__spec-icon" />
                  <span>{engineCC}cc</span>
                </div>
                <span className="car-card__spec-icon">|</span>
              </>
            )}

            {/* Year */}
            <div className="car-card__spec">
              <Calendar className="car-card__spec-icon" />
              <span>{formatRegYear(car)}</span>
            </div>
            <span className="car-card__spec-icon">|</span>

            {/* Transmission */}
            <div className="car-card__spec">
              <Cog className="car-card__spec-icon" />
              <span>
                {translateVehicleValue(car.transmission, 'transmission')}
              </span>
            </div>
            <span className="car-card__spec-icon">|</span>

            {/* Fuel Type */}
            <div className="car-card__spec">
              <Fuel className="car-card__spec-icon" />
              <span>{translateVehicleValue(car.fuelType, 'fuelType')}</span>
            </div>

            {/* Mileage (всегда последний) */}
            <span className="car-card__spec-icon">|</span>
            <div className="car-card__spec">
              <MapPin className="car-card__spec-icon" />
              <span>
                {car.mileage != null && car.mileage >= 0
                  ? `${plainNumber(car.mileage)} km`
                  : t('landing.card.notAvailable', 'N/A')}
              </span>
            </div>
          </div>
        </div>

        {/* PCM mode (#828, per-embed `showMonthlyRates`): британо-форсовый embed
            показывает месячный платёж как headline + полную DF-цену в скобках.
            По решению задачи в этом layout НЕ показываем strikethrough old price.
            Вне PCM-режима — стандартная цена (поведение без изменений). */}
        {showMonthlyRates && displayPrice > 0 ? (
          <div className="car-card__footer">
            <div className="car-card__footer-row">
              {/* "from" + "/mo*" — мельче суммы, в том же brand-цвете (наследуют
                  car-card__price). Сама сумма платежа — как обычная full-price:
                  text-xl bold. */}
              <p className="car-card__price">
                <span className="car-card__price--sm">from </span>
                <span className="car-card__price">
                  €{calculateMonthlyPayment(financedAmount).toLocaleString()}
                </span>
                <span className="car-card__price--sm">/mo*</span>
              </p>
              <Button
                size="sm"
                className="cta-button car-card__cta"
                onClick={handleCarClick}
              >
                {t('landing.card.viewDetails', 'View details')}
              </Button>
            </div>
            {/* Полная цена в скобках, "+ reg" внутри скобок и мельче для контраста.
                Для new-model (Autodis) "+reg" не показываем — цена уже финальная (#1086). */}
            <div className="car-card__note car-card__note--muted">
              (<span>€{displayPrice.toLocaleString()}</span>
              {!car.registrationStatus && !car.isNewModel && (
                <span> + reg</span>
              )}
              )
            </div>
          </div>
        ) : (
          <div className="car-card__footer">
            <div className="car-card__footer-row">
              <p className="car-card__price">
                {displayPrice > 0
                  ? money(displayPrice)
                  : t('landing.card.contactForPrice', 'Contact for price')}
                {/* new-model (Autodis): "+reg" не показываем — цена финальная (#1086). */}
                {!car.registrationStatus &&
                  !car.isNewModel &&
                  displayPrice > 0 && (
                    <span className="car-card__note">{showcaseText('showcase.car.reg', '+reg')}</span>
                  )}
              </p>
              <Button
                size="sm"
                className="cta-button car-card__cta"
                onClick={handleCarClick}
              >
                {t('landing.card.viewDetails', 'View details')}
              </Button>
            </div>
            {oldPrice ? (
              <div className="car-card__note">
                <span className="car-card__price-old">
                  {money(oldPrice)}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

CarCard.propTypes = {
  car: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    make: PropTypes.string,
    model: PropTypes.string,
    name: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.number,
    oldPrice: PropTypes.number,
    mileage: PropTypes.number,
    transmission: PropTypes.string,
    fuelType: PropTypes.string,
    color: PropTypes.string,
    engineDisplacement: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    registrationStatus: PropTypes.bool,
    statusSold: PropTypes.string,
    isHotOffer: PropTypes.bool,
    images: PropTypes.arrayOf(PropTypes.string),
    dutyFree: PropTypes.bool,
    dutyFreePrice: PropTypes.number,
  }).isRequired,
  forceShowSoldBadge: PropTypes.bool,
  // Mini-catalog mode (#828): если передан — вызывается вместо react-router navigate.
  // Используется для hard redirect на каталог клиентского сайта при встраивании виджета
  // на главную/промо-страницы.
  onCardClick: PropTypes.func,
  // Per-embed PCM mode (#828): когда true — карточка показывает "from €X/mo*"
  // как headline + полную цену в скобках (British Forces finance showcase).
  showMonthlyRates: PropTypes.bool,
  // false — свайп по фото не листает галерею, жест остаётся у прокрутки
  // вокруг карточки (горизонтальная лента на главной, #1456).
  swipePhotos: PropTypes.bool,
};

export default React.memo(CarCard);
